import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faSun, faMoon } from '@fortawesome/free-solid-svg-icons';
import { getAuth, signOut } from 'firebase/auth';
import TopBar from '../TopBar';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import UserContext from '../../context/UserContext';
import TourContext from '../../context/TourContext';
import { useTheme } from '../../context/ThemeContext';
import styles from './ProfileSettings.module.css';
import AuthCheckWrapper from '../AuthCheckWrapper';
import { clearNotificationCaches } from '../../utils/NotificationService';

const ProfileSettings = () => {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate();
  const { userData, clearCache: clearUserCache } = useContext(UserContext);
  const { cleanupAllTourListeners, clearCache: clearTourCache } = useContext(TourContext);
  const { isDark, toggleTheme } = useTheme();

  // Add event listener for window resize
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      
      // Create a promise-based cleanup sequence
      const cleanupSequence = async () => {
        console.log('Starting cleanup sequence...');
        
        // Step 1: Clean up all tour listeners
        console.log('Cleaning up tour listeners...');
        await new Promise((resolve) => {
          cleanupAllTourListeners();
          setTimeout(resolve, 100); // Give listeners time to cleanup
        });
        
        // Step 2: Clear all cached data
        console.log('Clearing caches...');
        await new Promise((resolve) => {
          clearUserCache();
          clearTourCache();
          clearNotificationCaches();
          setTimeout(resolve, 100); // Give cache clearing time to complete
        });
        
        // Step 3: Clear localStorage with user-specific approach
        console.log('Clearing localStorage...');
        await new Promise((resolve) => {
          const keysToPreserve = ['theme']; // Add any keys you want to keep across logins
          
          Object.keys(localStorage).forEach(key => {
            if (!keysToPreserve.includes(key)) {
              localStorage.removeItem(key);
            }
          });
          setTimeout(resolve, 50);
        });
        
        // Step 4: Clear sessionStorage
        console.log('Clearing sessionStorage...');
        await new Promise((resolve) => {
          sessionStorage.clear();
          setTimeout(resolve, 50);
        });
        
        console.log('Cleanup sequence completed');
      };
      
      // Execute cleanup sequence
      await cleanupSequence();
      
      // Step 5: Sign out from Firebase Auth only after cleanup is complete
      console.log('Signing out from Firebase...');
      const auth = getAuth();
      await signOut(auth);
      
      console.log('Logout completed successfully');
      
      // Step 6: Navigate to login page only after everything is complete
      navigate('/login');
      
    } catch (error) {
      console.error('Error during logout:', error);
      alert('There was an error logging out. Please try again.');
      setIsLoggingOut(false);
    }
  };

  const getValueColor = (value, paidValue, verifiedValue, pendingValue) => {
    if (value === verifiedValue) {
      return '#10b981'; // green
    } else if (value === pendingValue) {
      return '#f59e0b'; // orange
    } else {
      return '#ef4444'; // red
    }
  };

  const handleVerificationClick = () => {
    if (userData?.membershipTier === 'Free') {
      navigate('/membership');
    } else {
      navigate('/verification');
    }
  };

  // Create the menu options array, with conditional theme toggle for mobile
  const menuOptions = [
    { 
      title: 'Account Details', 
      path: '/account-details'
    },
    {
      title: 'Verification Status',
      value: userData?.idVerificationStatus || 'Not Verified',
      valueColor: getValueColor(
        userData?.idVerificationStatus, 
        '', 
        'Verified', 
        'Pending'
      ),
      onClick: handleVerificationClick
    },
    ...(isMobile ? [{ 
      title: `${isDark ? 'Light' : 'Dark'} Mode`,
      icon: isDark ? faSun : faMoon,
      onClick: toggleTheme,
      mobileOnly: true
    }] : []),
    { 
      title: 'Help Centre', 
      path: '/help-centre'
    },
    { 
      title: 'Terms of Service', 
      path: '/terms-of-service'
    },
    { 
      title: 'Privacy Policy', 
      path: '/privacy-policy'
    },
    { 
      title: 'Logout', 
      onClick: handleLogout 
    }
  ];

  if (isLoggingOut) {
    return (
      <div className={styles.loadingOverlay}>
        <div className={styles.loadingSpinner} />
        <div className={styles.loadingText}>Logging out...</div>
      </div>
    );
  }

  return (
    <div className={styles.mainLayout}>
      
      <LeftMenu />
      <div className={styles.profileSettingsMain}>
      <AuthCheckWrapper>
        <div className={styles.profileSettingsContent}>
          <div className={styles.profileSettingsHeader}>
            <h1 className={styles.profileSettingsTitle}>Profile Settings</h1>
          </div>

          <div className={styles.menuOptions}>
            {menuOptions.map((option, index) => (
              <div key={index} className={option.mobileOnly ? styles.mobileOnlyOption : ''}>
                {option.onClick ? (
                  <button
                    className={styles.menuItem}
                    onClick={option.onClick}
                  >
                    <div className={styles.menuItemContent}>
                      <span className={styles.menuItemText}>{option.title}</span>
                      {option.value && (
                        <span 
                          className={styles.menuItemValue}
                          style={{ color: option.valueColor }}
                        >
                          {option.value}
                        </span>
                      )}
                    </div>
                    {option.icon ? (
                      <FontAwesomeIcon icon={option.icon} className={styles.menuItemIcon} />
                    ) : (
                      <FontAwesomeIcon icon={faArrowRight} className={styles.menuItemIcon} />
                    )}
                  </button>
                ) : (
                  <Link
                    to={option.path}
                    className={styles.menuItem}
                  >
                    <div className={styles.menuItemContent}>
                      <span className={styles.menuItemText}>{option.title}</span>
                      {option.value && (
                        <span 
                          className={styles.menuItemValue}
                          style={{ color: option.valueColor }}
                        >
                          {option.value}
                        </span>
                      )}
                    </div>
                    <FontAwesomeIcon icon={faArrowRight} className={styles.menuItemIcon} />
                  </Link>
                )}
              </div>
            ))}
          </div>

          <div className={styles.versionInfo}>
            <span className={styles.versionText}>Version 1.0</span>
          </div>
        </div>
        </AuthCheckWrapper>
      </div>
      <RightMenu />
    </div>
  );
};

export default ProfileSettings;